// Profile's photo (Account.dc.html): the 72px gold ring with their initials, and for Accelerator
// members their photo in it and Change photo beside it (Adam, 2 Oct: back for the Accelerator's
// member directory). photo.js sends it: JPEG, PNG or WebP up to 2 MB, straight into private storage,
// then shown through a link signed for 2 minutes. While staff view as a member, the initials show
// (sign-file opens nothing then) and Change photo is off.
import { useEffect, useRef, useState } from 'react'
import { HubFunctionError } from '../lib/functions.js'
import { readOnlyControl } from '../lib/viewAs.jsx'
import { initials as initialsOf } from '../lib/format.js'
import { COPY } from './copy.js'
import { PHOTO_ACCEPT, PHOTO_MAX_BYTES, photoType, signPhoto, uploadPhoto } from './photo.js'
import './profile-directory.css'

export function ProfilePhoto({ person, canChange, readOnly, onPerson }) {
  const [url, setUrl] = useState(null)
  const [state, setState] = useState({ status: 'idle', line: null })
  const input = useRef(null)
  const path = person.photo_path || null

  // their photo, when they have 1 (nothing is asked while viewing as: sign-file would refuse)
  useEffect(() => {
    if (!canChange || !path || readOnly) { setUrl(null); return undefined }
    let live = true
    signPhoto(person.id).then(u => { if (live) setUrl(u) })
    return () => { live = false }
  }, [canChange, path, readOnly, person.id])

  async function pick(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || readOnly || state.status === 'busy') return
    const type = photoType(file)
    if (!type) { setState({ status: 'idle', line: COPY.photoType }); return }
    if (file.size > PHOTO_MAX_BYTES) { setState({ status: 'idle', line: COPY.photoTooBig }); return }
    if (!file.size) { setState({ status: 'idle', line: COPY.photoFailed }); return }
    setState({ status: 'busy', line: null })
    try {
      const done = await uploadPhoto(file, type)
      const next = done?.photo_path || path
      // a new path (each photo has its own name) is signed by the effect above, once
      if (next === path) setUrl(await signPhoto(person.id))
      onPerson({ photo_path: next })
      setState({ status: 'added', line: null })
    } catch (err) {
      setState({ status: 'idle', line: err instanceof HubFunctionError ? err.message : COPY.photoFailed })
    }
  }

  return (
    <div className="ac-photo-row">
      <span className={url ? 'ac-photo ac-photo--img' : 'ac-photo'} aria-hidden="true">
        {url ? <img src={url} alt="" onError={() => setUrl(null)} /> : initialsOf(person.full_name)}
      </span>
      {canChange && (
        <span className="ac-photo-change">
          <button type="button" className="ac-btn ac-photo-btn" onClick={() => input.current?.click()}
            aria-busy={state.status === 'busy' || undefined} {...readOnlyControl(readOnly)}>
            {state.status === 'busy' ? COPY.photoAdding : COPY.changePhoto}
          </button>
          <input ref={input} type="file" accept={PHOTO_ACCEPT} className="ac-photo-input" tabIndex={-1} aria-hidden="true" onChange={pick} />
          {state.status === 'added' && <span className="ac-msg" role="status">{COPY.photoAdded}</span>}
          {state.line && <span className="ac-msg ac-msg--error" role="alert">{state.line}</span>}
        </span>
      )}
    </div>
  )
}
